import React, { useEffect, useRef } from 'react'
import { Channel } from 'twilio-chat'
import { useQuery } from 'react-query'
import clsx from 'clsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFile } from '@fortawesome/pro-regular-svg-icons'

import chatService from '../../services/chatService'

type ActiveChatProps = {
  channel: Channel
  userName: string
  avatar: string
}

const ActiveChat: React.FC<ActiveChatProps> = (props) => {
  const messages = useQuery(
    ['chat-messages', props.channel.sid],
    () => chatService.getMessagesByChannel(props.channel),
    {
      onSuccess: () => {
        setTimeout(() => {
          scrollToLastMessage()
        }, 300)
      },
    }
  )
  const lastMessageRef = useRef<any>()

  const scrollToLastMessage = () => {
    if (lastMessageRef && lastMessageRef !== null && lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }

  useEffect(() => {
    if (props.channel) {
      // Subscribe to new messages
      props.channel.on('messageAdded', () => {
        messages.refetch()
      })
    }
  })

  useEffect(() => {
    scrollToLastMessage()
  })

  return (
    <div>
      <div className="relative flex items-center border-b border-gray-300 p-3">
        {props.avatar ? (
          <img className="h-12 w-12 rounded-full object-cover" src={props.avatar} alt={props.userName} />
        ) : (
          <div className="block h-12 w-12 rounded-full bg-gray-300" />
        )}
        <span className="ml-2 block font-bold text-gray-600">{props.userName}</span>
      </div>

      <div className="relative h-[40rem] w-full overflow-y-auto p-6">
        {messages.isLoading ? (
          <div className="animate-pulse space-y-2">
            <div className="h-8 w-48 rounded bg-gray-100" />
            <div className="h-8 w-96 rounded bg-gray-100" />
          </div>
        ) : (
          <ul className="space-y-2">
            {messages.data?.map((message) => {
              const isImage = ['image/jpeg', 'image/jpg', 'image/gif', 'image/png'].includes(message.mediaType)
              return (
                <li
                  title={`${message.created.toLocaleDateString('en-GB')} ${message.created.toLocaleTimeString(
                    'en-GB'
                  )}`}
                  key={message.sid}
                  ref={message.lastMessage ? lastMessageRef : null}
                  className={clsx('flex', {
                    'justify-start': message.author.startsWith('EndUser:'),
                    'justify-end': message.author.startsWith('CustomerUser:'),
                  })}
                >
                  <div
                    className={clsx('relative max-w-xl rounded-md px-4 py-2', {
                      'bg-gray-50 text-gray-700': message.author.startsWith('EndUser:'),
                      'bg-brand-500 text-white': message.author.startsWith('CustomerUser:'),
                    })}
                  >
                    {message.mediaType ? (
                      isImage ? (
                        <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
                          <img src={message.mediaUrl} alt={message.mediaFilename} width="50" />
                        </a>
                      ) : (
                        <p>
                          <FontAwesomeIcon icon={faFile} className="leading-icon" />
                          <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer" className="text-sm">
                            {`File: ${message.mediaFilename} - ${(Math.round(message.mediaSize / 1024), 2)}MB`}
                          </a>
                        </p>
                      )
                    ) : (
                      <span className="block">{message.body}</span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export default ActiveChat
